import AddIcon from "@mui/icons-material/Add";
import { Inventory2Outlined, ListAltOutlined, PrintOutlined } from "@mui/icons-material";
import { Box, Typography } from "@mui/material";
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from "react-router-dom";
import { Toolbar } from "../../components/ui/toolbar/toolbar.component";
import { Action } from "../../types/toolbar";
import CustomModal from "../../components/ui/modal/modal.component";
import { useState } from "react";
import EquipmentForm from "../../components/ui/equipmentForm/equipmentForm.component";
import EquipmentCounterForm from "../../components/ui/equipmentCounterForm/equipmentCounterForm.component";
import { useHasPermission } from "../../contexts/auth";
import { serviceActionsSectionStyle } from "../../styles/commonStyles";

export const EquipmentsActions = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [addEquipmentOpened, setAddEquipmentOpened] = useState<boolean>(false);
  const [addCounterOpened, setAddCounterOpened] = useState<boolean>(false);
  const canAddEquipment = useHasPermission("settlement.add_equipment");
  const canAddCounter = useHasPermission("settlement.add_equipmentcounter");

  const actions: Action[] = [
    {
      id: "add-new-equipment",
      icon: <AddIcon />,
      label: t("Add new equipment"),
      onClick: () => { setAddEquipmentOpened(true); },
      disabled: !canAddEquipment,
    },
    {
      id: "add-new-counter",
      icon: <AddIcon />,
      label: t("Add new counter"),
      onClick: () => { setAddCounterOpened(true); },
      disabled: !canAddCounter,
    },
  ];

  // Written into the URL like the contract quick filters, so the chosen view
  // survives a reload; the list page reads `is_archived` back out of it. No
  // param means "All equipment", with archived rows grayed out.
  const archivedParam = new URLSearchParams(location.search).get("is_archived") || "";

  const updateArchivedParam = (value?: string) => {
    const nextParams = new URLSearchParams(location.search);
    if (value) {
      nextParams.set("is_archived", value);
    } else {
      nextParams.delete("is_archived");
    }
    const nextSearch = nextParams.toString();
    navigate({ pathname: location.pathname, search: nextSearch ? `?${nextSearch}` : "" });
  };

  const quickFilterActions: Action[] = [
    {
      icon: <ListAltOutlined />,
      label: t("All equipment"),
      onClick: () => updateArchivedParam(undefined),
      color: !archivedParam ? "var(--primaryColor)" : undefined,
    },
    {
      icon: <PrintOutlined />,
      label: t("Active equipment"),
      onClick: () => updateArchivedParam("false"),
      color: archivedParam === "false" ? "var(--primaryColor)" : undefined,
    },
    {
      icon: <Inventory2Outlined />,
      label: t("Archived"),
      onClick: () => updateArchivedParam("true"),
      color: archivedParam === "true" ? "var(--primaryColor)" : undefined,
    },
  ];

  const handleEquipmentAdded = () => {
    setAddEquipmentOpened(false);
    window.dispatchEvent(new Event('equipmentAdded'));
  };

  const handleCounterAdded = () => {
    setAddCounterOpened(false);
    window.dispatchEvent(new Event("equipmentCounterAdded"));
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          height: "100%",
          minHeight: 0,
        }}
      >
        <Box component="fieldset" sx={serviceActionsSectionStyle}>
          <Typography component="legend" variant="subtitle2" sx={{ px: 1, mb: 1 }}>
            {t("Quick filters")}
          </Typography>
          <Toolbar actions={quickFilterActions} noTopPadding showShortcut={false} />
        </Box>
        <Box sx={{ flex: 1, minHeight: 0 }}>
          <Toolbar actions={actions} noTopPadding />
        </Box>
      </Box>
      <CustomModal
        opened={addEquipmentOpened}
        setOpened={setAddEquipmentOpened}
        title={t("Add new equipment")}
        content={
          <EquipmentForm
            onSuccess={handleEquipmentAdded}
          />
        }
      />
      <CustomModal
        opened={addCounterOpened}
        setOpened={setAddCounterOpened}
        title={t("Add new counter")}
        content={
          <EquipmentCounterForm
            onSuccess={handleCounterAdded}
            hiddenFields={["job"]}
          />
        }
      />
    </>
  );
};
